import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { BoDialogComponent } from '@3ddv/software-division-components/backoffice/dialog';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { Adlink } from 'src/app/private/adlink/models/adlink.model';
import { AdlinkService } from 'src/app/private/adlink/services/adlink.service';
import { ApiResponse } from 'src/app/private/models/api.model';
import { Upgrade } from 'src/app/private/upgrade/models/upgrade.model';
import { UpgradeService } from 'src/app/private/upgrade/services/upgrade.service';
import { Upsell } from 'src/app/private/upsell/models/upsell.model';
import { UpsellService } from 'src/app/private/upsell/services/upsell.service';
import { DialogCloseReason } from '../../../layout/dialog/app-dialog.service';
import { Event } from '../../models/event.model';
import { EventsService } from '../../services/events.service';
import { FormsModule } from '@angular/forms';
import { TitleCasePipe } from '@angular/common';
import { GsSelectableListComponent } from 'src/app/shared/ui/selectable-list/gs-selectable-list.component';

const MAX_ADLINKS_PER_EVENT = 2;
const ADLINK_LIMIT_MESSAGE = `An event can have at most ${MAX_ADLINKS_PER_EVENT} ad links.`;

@Component({
  selector: 'app-edit',
  templateUrl: './edit.component.html',
  imports: [
    FormsModule,
    TitleCasePipe,
    BoDialogComponent,
    ButtonComponent,
    GsSelectableListComponent,
  ],
})
export class EditComponent implements OnInit, OnDestroy {
  private dialogRef = inject<BrnDialogRef<DialogCloseReason>>(BrnDialogRef);
  private eventsService = inject(EventsService);
  private adlinkService = inject(AdlinkService);
  private upsellService = inject(UpsellService);
  private upgradeService = inject(UpgradeService);
  private toastr = inject(ToastrService);

  @Input() type: 'upgrade' | 'upsell' | 'adlink';
  @Input() event: Event;
  public selectedIds: number[] = [];
  public upsellList: ApiResponse<Upsell[]>;
  public upgradeList: ApiResponse<Upgrade[]>;
  public adLinkList: ApiResponse<Adlink[]>;
  public isListLoading = false;
  public adlinkLimitReached = false;
  private subscriptions: Array<Subscription>;

  constructor() {
    this.subscriptions = [];

    this.upsellList = {
      metadata: {
        total: 0,
        perPage: 0,
        currentPage: 1,
        lastPage: 1,
        firstPage: 1,
        hasMorePages: false,
      },
      data: [],
      message: 'OK',
    };
    this.upgradeList = {
      metadata: {
        total: 0,
        perPage: 0,
        currentPage: 1,
        lastPage: 1,
        firstPage: 1,
        hasMorePages: false,
      },
      data: [],
      message: 'OK',
    };
    this.adLinkList = {
      metadata: {
        total: 0,
        perPage: 0,
        currentPage: 1,
        lastPage: 1,
        firstPage: 1,
        hasMorePages: false,
      },
      data: [],
      message: 'OK',
    };
  }

  ngOnInit(): void {
    this.isListLoading = true;
    if (this.type === 'upgrade') {
      const upgrade$ = this.upgradeService.getAll$(1).subscribe({
        next: res => {
          this.upgradeList = res;
          this.isListLoading = false;
        },
        error: () => {
          this.isListLoading = false;
        },
      });
      this.subscriptions.push(upgrade$);
    } else if (this.type === 'upsell') {
      const upsell$ = this.upsellService.getAll$(1).subscribe({
        next: res => {
          this.upsellList = res;
          this.isListLoading = false;
        },
        error: () => {
          this.isListLoading = false;
        },
      });
      this.subscriptions.push(upsell$);
    } else if (this.type === 'adlink') {
      const adlink$ = this.adlinkService.getAll$(1).subscribe({
        next: res => {
          this.adLinkList = res;
          this.isListLoading = false;
          this.refreshAdlinkDisabledIds();
        },
        error: () => {
          this.isListLoading = false;
        },
      });
      this.subscriptions.push(adlink$);
    }

    const relatedItems = this.event[`${this.type}s`] as
      Array<{ id: number }> | undefined;
    if (relatedItems && relatedItems.length) {
      this.selectedIds = relatedItems.map(item => item.id);
    }
    this.refreshAdlinkDisabledIds();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public adlinkDisabledIds: number[] = [];

  public onSelectionChange(ids: number[]): void {
    this.selectedIds = ids;
    this.refreshAdlinkDisabledIds();
  }

  private refreshAdlinkDisabledIds(): void {
    if (
      this.type !== 'adlink' ||
      this.selectedIds.length < MAX_ADLINKS_PER_EVENT
    ) {
      this.adlinkDisabledIds = [];
      return;
    }

    this.adlinkDisabledIds = (this.adLinkList?.data ?? [])
      .filter(item => !this.selectedIds.includes(item.id))
      .map(item => item.id);
  }

  public onDismiss(reason: DialogCloseReason) {
    this.dialogRef.close(reason);
  }

  public onConfirm() {
    if (this.type === 'adlink') {
      if (this.selectedIds.length > MAX_ADLINKS_PER_EVENT) {
        this.adlinkLimitReached = true;
        this.toastr.error(ADLINK_LIMIT_MESSAGE);
        return;
      }
    }

    const u$ = this.eventsService
      .update$(this.event.id, this.type, this.selectedIds)
      .subscribe({
        next: response => {
          this.event = response.data;
          this.onDismiss('success');
        },
        error: err => {
          const message = this.getApiErrorMessage(err);
          if (this.type === 'adlink') {
            this.adlinkLimitReached = true;
          }
          this.toastr.error(message);
        },
      });
    this.subscriptions.push(u$);
  }

  private getApiErrorMessage(err: unknown): string {
    const errorBody = (err as { error?: Record<string, unknown> })?.error;

    if (
      Array.isArray(errorBody?.['errors']) &&
      errorBody['errors'].length > 0
    ) {
      return String(
        (errorBody['errors'][0] as { message?: string }).message ??
          ADLINK_LIMIT_MESSAGE
      );
    }

    const nestedError = errorBody?.['error'] as
      { message?: string } | undefined;
    if (nestedError?.message) {
      return nestedError.message;
    }

    if (typeof errorBody?.['message'] === 'string') {
      return errorBody['message'];
    }

    return ADLINK_LIMIT_MESSAGE;
  }
}
